import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { URLS } from '@wix/da-url';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import FavouriteOutfilled from '@wix/da-ds/pkg/Icons/FavouriteOutfilled/FavouriteOutfilled';
import { BiData, SubmitButtonClickBiEvent } from '@wix/da-bi/pkg/events';
import { isGroup } from '@wix/da-shared-react/pkg/User/helpers';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton/GhostButton';
import MediaSelectorWithGroupGallection from '@wix/da-shared-react/pkg/MediaPopup/Groups/MediaSelectorWithGroupGallection';
import { MediaSourceType } from '@wix/da-shared-react/pkg/MediaPopup/types';
import { GroupGallectionType } from '@wix/da-shared-react/pkg/GroupGallectionSelector/redux/types';

import EmptyState from '../../../EmptyState';
import { GallectionType } from '../../../../../types/gallection';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import { getSubmitUrlForProfileOwner } from '../../../../utils/urls';
import s from './GallectionsEmptyState.scss';

const TRANSLATION_KEYS = {
  [GallectionType.GALLERY]: {
    titleOwner: 'pages.gallections.gallery.emptyState.title.owner',
    titleVisitor: 'pages.gallections.gallery.emptyState.title.visitor',
    subtitleOwner: 'pages.gallections.gallery.emptyState.subtitle.owner',
    subtitleVisitor: 'pages.gallections.gallery.emptyState.subtitle.visitor',
    buttonCTA: 'pages.gallections.gallery.emptyState.buttonCTA.owner',
  },
  [GallectionType.COLLECTION]: {
    titleOwner: 'pages.gallections.collection.emptyState.title.owner',
    titleVisitor: 'pages.gallections.collection.emptyState.title.visitor',
    subtitleOwner: 'pages.gallections.collection.emptyState.subtitle.owner',
    subtitleVisitor: 'pages.gallections.collection.emptyState.subtitle.visitor',
    buttonCTA: 'pages.gallections.collection.emptyState.buttonCTA.owner',
  },
  [GallectionType.PRIVATE_COLLECTION]: {
    titleOwner: 'pages.gallections.privateCollection.emptyState.title.owner',
    subtitleOwner:
      'pages.gallections.privateCollection.emptyState.subtitle.owner',
    buttonCTA: 'pages.gallections.privateCollection.emptyState.buttonCTA.owner',
  },
};

export interface Props {
  gallectionType: GallectionType;
  owner: PapiUser;
  isOwner: boolean;
  canSubmitDeviations: boolean;
  contributeDeviations: (
    deviations: PapiDeviation[],
    group: PapiUser,
    type: GroupGallectionType,
    folderId?: number
  ) => void;
}

function getSubtitle(
  gallectionType: GallectionType,
  t: (key: string, opts?: any) => string,
  owner: PapiUser,
  canSubmitDeviations: boolean
) {
  if (canSubmitDeviations) {
    return gallectionType === GallectionType.COLLECTION
      ? t(TRANSLATION_KEYS[gallectionType].subtitleOwner, {
          postProcess: ['reactPostprocessor', 'featureBrancher'],
          starIcon: (
            <FavouriteOutfilled size={IconSize.SMALL} className={s['star']} />
          ),
        })
      : t(TRANSLATION_KEYS[gallectionType].subtitleOwner);
  }
  return t(TRANSLATION_KEYS[gallectionType].subtitleVisitor, {
    username: owner.username,
  });
}

function GallectionsEmptyState(props: Props) {
  const { t } = useTranslation();
  const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
  const {
    isOwner,
    canSubmitDeviations,
    gallectionType,
    owner,
    contributeDeviations,
  } = props;

  const onButtonClick = useCallback(() => {
    if (gallectionType === GallectionType.GALLERY) {
      window.location.replace(getSubmitUrlForProfileOwner(owner));
    } else {
      window.location.replace(URLS.ROOT);
    }
  }, [gallectionType, owner]);

  const openMediaSelector = useCallback(() => {
    setIsMediaSelectorOpen(true);
  }, []);

  const closeMediaSelector = useCallback(() => {
    setIsMediaSelectorOpen(false);
  }, []);

  const getSecondaryButton = () => {
    const isGroupGallery =
      gallectionType === GallectionType.GALLERY && isGroup(owner);

    if (!canSubmitDeviations || !isGroupGallery) {
      return null;
    }

    return (
      <>
        <GhostButton variant="main" size="medium" onClick={openMediaSelector}>
          {t(
            canSubmitDeviations
              ? 'groups.submit.contributeExistingDeviation'
              : 'groups.submit.recommendExistingDeviation'
          )}
        </GhostButton>
        {isMediaSelectorOpen && (
          <MediaSelectorWithGroupGallection
            group={owner}
            allowUploads={false}
            mediaSourceTypes={[
              MediaSourceType.GALLERY,
              MediaSourceType.COLLECTION,
              MediaSourceType.JOURNAL,
              MediaSourceType.STATUS,
              MediaSourceType.BROWSE,
            ]}
            onClose={closeMediaSelector}
            onSelect={(...args: any[]) => {
              contributeDeviations(
                ...(args as [
                  PapiDeviation[],
                  PapiUser,
                  GroupGallectionType,
                  number?,
                ])
              );
              closeMediaSelector();
              window.location.reload();
            }}
          />
        )}
      </>
    );
  };

  if (gallectionType === GallectionType.PRIVATE_COLLECTION && !isOwner) {
    return null;
  }

  return (
    <EmptyState
      view="large"
      className={s['root']}
      title={
        canSubmitDeviations
          ? t(TRANSLATION_KEYS[gallectionType].titleOwner)
          : t(TRANSLATION_KEYS[gallectionType].titleVisitor, {
              username: owner.username,
            })
      }
      subtitle={getSubtitle(gallectionType, t, owner, canSubmitDeviations)}
      buttonVariant="approval-green"
      buttonLabel={t(TRANSLATION_KEYS[gallectionType].buttonCTA)}
      biData={BiData<SubmitButtonClickBiEvent>({
        evid: 704,
        sectionname: gallectionType,
      })}
      onButtonClick={onButtonClick}
      withButton={canSubmitDeviations}
      customSecondaryButton={getSecondaryButton()}
      buttonAlignment="vertical"
    />
  );
}

export default GallectionsEmptyState;
